import type { PetWithStatus } from '@shared/domain/petModels';
import { useCallback, useEffect, useState } from 'react';
import { useAccountId } from '../auth/useAccountId';
import { fetchOwnerPets } from '../api/petBackendApi';

interface UseUserPetOptions {
  /** Enable auto-refresh polling (default: false) */
  enablePolling?: boolean;
  /** Polling interval in milliseconds (default: 120000 = 2 minutes) */
  pollingInterval?: number;
  /**
   * Override the address pets are fetched for. Pass the guest sentinel
   * (`useWalletConnection().guestWallet`) so free-play guests (no identity yet)
   * see their free pet. Defaults to the authenticated identity address.
   */
  address?: string | null;
}

export function useUserPet(options?: UseUserPetOptions) {
  const { enablePolling = false, pollingInterval = 120_000 } = options ?? {};
  // Identity address (Stage B3): the Sui/EVM address proven at login, or the connected
  // Sui wallet when no session yet. Pets are fetched by this address, unless the caller
  // passes an override (e.g. the guest sentinel wallet).
  const { address: identityAddress } = useAccountId();
  const address = options?.address !== undefined ? options.address : identityAddress;
  const [pets, setPets] = useState<PetWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  const loadPets = useCallback(async () => {
    if (!address) {
      setPets([]);
      return;
    }

    setIsLoading(true);
    try {
      const fetched = await fetchOwnerPets(address);
      setPets(fetched);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('[useUserPet] failed to load pets', error);
      setPets([]);
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  // Auto-refresh polling
  useEffect(() => {
    if (!enablePolling || !address) {
      return;
    }

    const intervalId = setInterval(() => {
      console.log('[useUserPet] Auto-refreshing pet data...');
      loadPets();
    }, pollingInterval);

    return () => clearInterval(intervalId);
  }, [enablePolling, pollingInterval, address, loadPets]);

  const petCount = pets.length;

  return {
    pet: pets[0] ?? null,
    pets,
    petCount,
    hasPet: petCount > 0,
    isLoading,
    lastRefreshTime,
    refetch: loadPets,
  };
}
